import { CommonModule } from '@angular/common';
import { Component, inject, Optional, Signal, signal, ViewChild, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { AttachmentListComponent } from '@app/components/addons/attachments/attachment-list/attachment-list.component';
import { InputFileComponent } from '../../inputs/input-file/input-file.component';
import { InputTextEditorComponent } from '../../inputs/input-text-editor/input-text-editor.component';
import { SvgComponent } from '../../svg/svg.component';
import { CacheService } from '@app/_services/cache.service';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { Attachment, Feedback, TextEditorActiveFormats } from '@app/_interfaces/generic.interface';
import { AlertService } from '@app/_services/alert.service';
import { getNewId } from '@app/_utils/utils';
import { FirebaseService } from '@app/_services/firebase.service';
import { DependencyService } from '@app/_services/dependency.service';
import { SettingsService } from '@app/_services/settings.service';
import { AttachmentsService } from '@app/_services/attachments.service';
import { textEditorDefaultActiveFormats } from '@app/_datas/const.data';
import { TextEditorOptionsComponent } from '../../text-editor-options/text-editor-options.component';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { Router } from '@angular/router';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
  selector: 'app-feedback-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SvgComponent,
    InputTextEditorComponent,
    AttachmentListComponent,
    InputFileComponent,
    TextEditorOptionsComponent
  ],
  templateUrl: './feedback-dialog.component.html',
  styleUrl: './feedback-dialog.component.scss'
})

export class FeedbackDialogComponent implements OnInit, OnDestroy {

  readonly BRAND_CONFIG = BRAND_CONFIG;

  @ViewChild('fbForm') fbForm!: NgForm;
  @ViewChild(InputTextEditorComponent) textEditor!: InputTextEditorComponent;
  unSubscribe = new Subject<void>();
  feedbackForm: FormGroup;
  newAttachments!: FormGroup;

  activeFormats: WritableSignal<TextEditorActiveFormats> = signal(textEditorDefaultActiveFormats);
  editorFocused: WritableSignal<boolean> = signal(false);
  dropdownPosition: WritableSignal<{ top: number; left: number }> = signal({ top: 0, left: 0 });
  screenEnterTime: Signal<Date> = signal(new Date());

  constructor(
    public cc: CacheService,
    private fb: FormBuilder,
    private alertService: AlertService,
    public ss: SettingsService,
    private attachmentsService: AttachmentsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    public router: Router,
  ) {
    this.newAttachments = this.fb.group({});
    this.feedbackForm = this.fb.group({
      feedbackText: new FormControl('', [Validators.maxLength(20000), Validators.required]),
      attachments: this.fb.group({}),
      emotion: new FormControl(-1),
      id: new FormControl(getNewId()),
      isStoreFeedbackGiven: new FormControl(null),
      uid: new FormControl(this.cc.user.uid),
      userName: new FormControl(this.cc.user.name),
    });
  }

  ngOnInit() {
    const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
    this.as.screenViewEvent('drawerFeedback', previousScreen);
  }

  navigateToToday() {
    this.router.navigate(['/today']);
  }

  getFc(name: string): FormControl {
    return this.feedbackForm.get(name) as FormControl;
  }

  get attachments(): FormGroup {
    return this.feedbackForm.get('attachments') as FormGroup;
  }

  get attachmentValues(): Attachment[] {
    const newAttachments = Object.values(this.newAttachments.value);
    const attachments = [...newAttachments];
    return attachments as Attachment[];
  }

  hasChanges(): boolean {
    return this.isValid() || Object.keys(this.newAttachments.value).length > 0;
  }

  isValid(): boolean {
    const feedbackText = this.feedbackForm.get('feedbackText')?.value;
    if (!feedbackText) return false;
    return feedbackText.trim() !== '';
  }

  async save() {
    this.cc.isLoading.set(true);
    const newAttachments: Attachment[] = Object.values(this.newAttachments.value);
    const attachments = await this.attachmentsService.updateAttachments(newAttachments, this.basePath);
    const attachmentIds = Object.keys(attachments.value);
    attachmentIds.forEach(id => {
      this.attachments.addControl(id, attachments.get(id));
    });
    // Clear newAttachments after successful upload to prevent duplicates in attachmentValues getter
    this.newAttachments = this.fb.group({});

    const feedback: Feedback = this.feedbackForm.value;
    await this.ss.sendFeedback(feedback);
    this.cc.isLoading.set(false);
    this.alertService.toast(
      this.ss.t('feedbackThankyouResponse_content', { brandName: BRAND_CONFIG.name }),
      'common_dismiss',
      '',
      true,
    );
    this.close();
  }

  get basePath(): string {
    return `supportApp/media/${this.cc.user.uid}/usersFeedback/${this.feedbackForm.value.id}`;
  }

  deleteAttachments(attachments: Attachment[]) {
    attachments?.forEach(attach => {
      this.newAttachments.removeControl(attach.id.toString());
    });
  }

  async closeDialog() {
    if (this.hasChanges()) {
      const res = await this.alertService.confirm(this.ss.t('discard_title'), this.ss.t('discard_content'), this.ss.t('common_discard'), this.ss.t('common_buttonCancel'), 'color-11', 'color-7');
      if (!res) return;
      if (res.confirm === true) {
        this.close();
      }
      return;
    } else {
      this.close();
    }
  }

  close() {
    this.router.navigate(['/today']);
  }

  focusEditor() {
    this.textEditor?.focusEditor();
  }

  blurEditor() {
    this.textEditor?.blurEditor();
  }

  onToolbarClose(event: { shouldBlur: boolean }) {
    if (event.shouldBlur) {
      this.blurEditor();
    }
  }

  onDropdownPosition(position: { top: number; left: number }) {
    this.dropdownPosition.set(position);
  }

  editOptions(opt: 'bold' | 'italic' | 'underline' | 'list', value?: 'ordered' | 'bullet') {
    // Apply format
    this.textEditor?.toggleFormat(opt, value);

    // Keep editor focused after formatting (both mobile and desktop)
    setTimeout(() => {
      this.textEditor?.focusEditor();
    }, 50);
  }

  ngOnDestroy() {
    this.as.screenExitEvent('drawerFeedback', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
